import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, Info, Sparkles, Loader2, FolderArchive } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Callout } from '@/components/ui/callout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { cn, appCategoryLabel } from '@/lib/utils'
import { assessmentsApi, documentsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { useEntitlements } from '@/hooks/use-entitlements'
import { useActiveDelegation } from '@/hooks/use-delegation'
import type { RegistryEntry, RegistryStato } from '@/types'

// Traduttore i18n nella firma minima usata in questo file
type Translator = (key: string, opts?: Record<string, unknown>) => string

// Demonstration-only data, shown to FREE accounts to illustrate what the
// institutional register looks like once the school is on a paid plan.
const righeDemo = (t: Translator): DisplayRow[] => [
  { key: 'chatgpt', strumento: 'ChatGPT Edu', fornitore: 'OpenAI', categoria: t('registro.demo.catContenuti'), stato: 'CON_LIMITAZIONI', data: '12/02/2026', note: t('registro.demo.noteChatgpt') },
  { key: 'gemini', strumento: 'Google Gemini for Education', fornitore: 'Google', categoria: t('registro.demo.catTutoring'), stato: 'AUTORIZZATO', data: '03/03/2026', note: t('registro.demo.noteGemini') },
  { key: 'copilot', strumento: 'Microsoft Copilot', fornitore: 'Microsoft', categoria: t('registro.demo.catAmministrazione'), stato: 'AUTORIZZATO', data: '21/01/2026', note: t('registro.demo.noteCopilot') },
  { key: 'canva', strumento: 'Canva Magic', fornitore: 'Canva', categoria: t('registro.demo.catContenuti'), stato: 'SOSPESO', data: '—', note: t('registro.demo.noteCanva') },
  { key: 'khanmigo', strumento: 'Khanmigo', fornitore: 'Khan Academy', categoria: t('registro.demo.catTutoring'), stato: 'VALIDATO_DPO', data: '—', note: t('registro.demo.noteKhanmigo') },
  { key: 'perplexity', strumento: 'Perplexity', fornitore: 'Perplexity AI', categoria: t('registro.demo.catRicerca'), stato: 'ANALIZZATO', data: '—', note: t('registro.demo.notePerplexity') },
]

interface DisplayRow {
  key: string
  strumento: string
  fornitore: string
  categoria: string
  stato: RegistryStato
  data: string
  note: string
  entry?: RegistryEntry
}

const STATO_CLASS: Record<RegistryStato, string> = {
  AUTORIZZATO: 'bg-verde text-white',
  CON_LIMITAZIONI: 'bg-giallo-dark text-white',
  SOSPESO: 'bg-giallo text-white',
  DA_RIVALUTARE: 'bg-mim-orange text-white',
  NON_AUTORIZZATO: 'bg-rosso text-white',
  VALIDATO_DPO: 'bg-mim-blue text-white',
  ANALIZZATO: 'bg-muted text-muted-foreground',
  REVOCATO: 'bg-rosso-dark text-white',
}

function incidentNote(e: RegistryEntry, t: Translator, locale: string): string | null {
  const inc = e.incident
  if (!inc) return null
  const opened = new Date(inc.opened_at).toLocaleDateString(locale)
  if (inc.stato === 'REVOCATO') {
    return inc.decided_at
      ? t('registro.incident.revokedOn', {
          date: new Date(inc.decided_at).toLocaleDateString(locale),
          reason: inc.reason,
        })
      : t('registro.incident.revoked', { reason: inc.reason })
  }
  if (inc.dpo_analyzed_at) {
    return t('registro.incident.analyzed', { date: opened })
  }
  return inc.opened_by_name
    ? t('registro.incident.waitingDpoBy', { date: opened, name: inc.opened_by_name })
    : t('registro.incident.waitingDpo', { date: opened })
}

// Stati che precedono la validazione della catena: per i profili senza ruolo
// di governance (docente/segreteria/ispettore) la colonna Validazione mostra un
// badge invece del "—", così il richiedente capisce che la sua richiesta è
// in lavorazione ("In attesa" per ANALIZZATO, "In attesa di autorizzazione"
// per VALIDATO_DPO, che attende solo la decisione del Dirigente). DPO/
// Dirigente conservano il "—": per loro il contesto è già dato dalla
// colonna Azioni.
const STATI_IN_ATTESA: RegistryStato[] = ['ANALIZZATO', 'VALIDATO_DPO']

type ActionKind = 'suspend' | 'dpo' | 'decide' | 'lift'

const ACTION_TITLE_KEYS: Record<ActionKind, string> = {
  suspend: 'registro.dialog.suspendTitle',
  dpo: 'registro.dialog.dpoTitle',
  decide: 'registro.dialog.decideTitle',
  lift: 'registro.dialog.liftTitle',
}

// isDirigenteEffective covers both the Dirigente and their active
// scope_incidenti delegate (P7): the backend (_governance_school with
// delegate_scope="incidenti") already lets the delegate suspend/decide/lift
// in their place, so the row actions must follow the same perimeter — the
// DPO step is never delegable (DPO non delegabile, decisione utente).
// label/info sono chiavi i18n, tradotte al render
function rowAction(
  e: RegistryEntry, isDpo: boolean, isDirigenteEffective: boolean
): { kind: ActionKind; label: string } | { info: string } | null {
  if (!isDpo && !isDirigenteEffective) return null
  const inc = e.incident
  if (!inc) return { kind: 'suspend', label: 'registro.actions.suspend' }
  if (inc.stato === 'SOSPESO' && !inc.dpo_analyzed_at) {
    return isDpo
      ? { kind: 'dpo', label: 'registro.actions.dpo' }
      : { info: 'registro.actions.waitDpo' }
  }
  if (inc.stato === 'SOSPESO') {
    return isDirigenteEffective
      ? { kind: 'decide', label: 'registro.actions.decide' }
      : { info: 'registro.actions.waitDs' }
  }
  // REVOCATO
  return isDirigenteEffective ? { kind: 'lift', label: 'registro.actions.lift' } : null
}

function RegistroTable({
  rows, isDpo = false, isDirigenteEffective = false, onAction,
}: {
  rows: DisplayRow[]
  isDpo?: boolean
  isDirigenteEffective?: boolean
  onAction?: (kind: ActionKind, entry: RegistryEntry) => void
}) {
  const { t } = useTranslation()
  const showActions = !!onAction && (isDpo || isDirigenteEffective)
  const showPendingBadge = !isDpo && !isDirigenteEffective
  const headers = [
    t('registro.headers.strumento'),
    t('registro.headers.fornitore'),
    t('registro.headers.categoria'),
    t('registro.headers.stato'),
    t('registro.headers.validazione'),
    t('registro.headers.note'),
  ]
  if (showActions) headers.push(t('registro.headers.azioni'))
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {headers.map((h) => (
              <th key={h} className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const action = showActions && r.entry
              ? rowAction(r.entry, isDpo, isDirigenteEffective)
              : null
            return (
              <tr key={r.key} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-3 pr-4 font-medium">{r.strumento}</td>
                <td className="py-3 pr-4 text-muted-foreground">{r.fornitore}</td>
                <td className="py-3 pr-4 text-muted-foreground">{r.categoria}</td>
                <td className="py-3 pr-4">
                  <Badge className={cn('text-xs', STATO_CLASS[r.stato])}>
                    {t(`registro.stato.${r.stato}` as any)}
                  </Badge>
                </td>
                <td className="py-3 pr-4 text-muted-foreground">
                  {showPendingBadge && r.data === '—' && STATI_IN_ATTESA.includes(r.stato) ? (
                    <Badge variant="outline" className="text-xs font-normal">
                      {t(r.stato === 'VALIDATO_DPO' ? 'registro.pendingAuth' : 'registro.pending')}
                    </Badge>
                  ) : (
                    r.data
                  )}
                </td>
                <td className="py-3 pr-4 text-xs text-muted-foreground">{r.note}</td>
                {showActions && (
                  <td className="py-3 text-xs">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {action && 'kind' in action ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => onAction!(action.kind, r.entry!)}
                        >
                          {t(action.label as any)}
                        </Button>
                      ) : action && 'info' in action ? (
                        <span className="text-muted-foreground italic">{t(action.info as any)}</span>
                      ) : null}
                      {r.entry?.app_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          title={t('registro.actions.dossierTitle')}
                          asChild
                        >
                          <a href={documentsApi.dossierUrl(r.entry.app_id)} download>
                            <FolderArchive className="w-3.5 h-3.5 mr-1" />
                            {t('registro.actions.dossier')}
                          </a>
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function Registro() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'it' ? 'it-IT' : 'en-GB'
  const { user } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: ent, isLoading: entLoading } = useEntitlements()
  const isPaid = !!ent && ent.plan !== 'FREE'

  // P7: the Dirigente's active scope_incidenti delegate acts on the incident
  // workflow (sospendi/decidi/ripristina) exactly like the Dirigente — the
  // backend already allows it (_governance_school, delegate_scope="incidenti").
  const delegation = useActiveDelegation()
  const isDpo = user?.role === 'DPO'
  const isDirigenteEffective =
    user?.role === 'DIRIGENTE'
    || (!!delegation?.scope_incidenti && delegation.school_id === user?.school_id)

  const [dialog, setDialog] = useState<{ kind: ActionKind; entry: RegistryEntry } | null>(null)
  const [notes, setNotes] = useState('')
  const [decision, setDecision] = useState<'RIPRISTINO' | 'REVOCA'>('RIPRISTINO')

  const { data: registry, isLoading: registryLoading } = useQuery({
    queryKey: ['registry'],
    queryFn: assessmentsApi.registry,
    enabled: isPaid,
    // Authorization state is compliance-critical: every visit to this page
    // must reflect the latest Dirigente decision, not the app-wide 2-minute
    // cache (main.tsx) used for lower-stakes data.
    staleTime: 0,
  })

  const closeDialog = () => {
    setDialog(null)
    setNotes('')
    setDecision('RIPRISTINO')
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!dialog) return
      const { kind, entry } = dialog
      if (kind === 'suspend') return assessmentsApi.suspendTool(entry.app_id!, notes)
      const incidentId = entry.incident!.id
      if (kind === 'dpo') return assessmentsApi.incidentDpoAnalysis(incidentId, notes)
      if (kind === 'decide') return assessmentsApi.incidentDecide(incidentId, decision, notes || undefined)
      return assessmentsApi.incidentLift(incidentId, notes)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registry'] })
      toast({ title: t('registro.toastOk') })
      closeDialog()
    },
    onError: (err: any) => {
      toast({
        title: t('registro.toastKo'),
        description: err?.response?.data?.detail ?? t('common.unexpectedError'),
        variant: 'destructive',
      })
    },
  })

  const notesRequired = dialog?.kind !== 'decide'
  const notesValid = !notesRequired || notes.trim().length >= 20

  const realRows: DisplayRow[] = (registry ?? []).map((e) => ({
    key: e.assessment_id,
    strumento: e.strumento,
    fornitore: e.fornitore || '—',
    categoria: appCategoryLabel(e.categoria, i18n.language),
    stato: e.stato,
    data: e.data_validazione
      ? new Date(e.data_validazione).toLocaleDateString(locale)
      : '—',
    note:
      incidentNote(e, t, locale) ??
      (e.stato === 'DA_RIVALUTARE'
        ? e.valid_until && new Date(e.valid_until) < new Date()
          ? t('registro.revalidationExpired', {
              date: new Date(e.valid_until).toLocaleDateString(locale),
            })
          : t('registro.revalidationEngine')
        : e.note || (e.validato_da ? t('registro.validatedBy', { name: e.validato_da }) : '')),
    entry: e,
  }))

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="it-page-title">
        <p className="text-[11px] font-bold uppercase tracking-wider text-mim-blue dark:text-primary mb-1">
          {t('registro.eyebrow')}
        </p>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-mim-blue dark:text-primary" />
          {t('registro.title')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t('registro.subtitle')}
        </p>
      </div>

      {entLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : !isPaid ? (
        <>
          <Callout variant="note" title={t('registro.demoTitle')} icon={<Sparkles className="w-4 h-4" />}>
            {t('registro.demoBody')}
          </Callout>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('registro.tableTitleDemo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RegistroTable rows={righeDemo(t)} />
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('registro.tableTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {registryLoading ? (
              <div className="space-y-2">
                {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
              </div>
            ) : realRows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Info className="w-8 h-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">
                  {t('registro.emptyTitle')}
                </p>
                <p className="text-xs mt-1">
                  {t('registro.emptyHint')}
                </p>
              </div>
            ) : (
              <RegistroTable
                rows={realRows}
                isDpo={isDpo}
                isDirigenteEffective={isDirigenteEffective}
                onAction={(kind, entry) => setDialog({ kind, entry })}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Incident workflow dialog (P4): suspension → DPO analysis → DS decision */}
      <Dialog open={!!dialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog ? `${t(ACTION_TITLE_KEYS[dialog.kind] as any)} — ${dialog.entry.strumento}` : ''}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              {dialog.kind === 'suspend' && (
                <p className="text-sm text-muted-foreground">
                  {t('registro.dialog.suspendInfo')}
                </p>
              )}
              {dialog.kind === 'decide' && (
                <>
                  {dialog.entry.incident?.dpo_notes && (
                    <div className="text-sm rounded-md border border-border bg-muted/40 p-3">
                      <p className="font-medium text-xs uppercase text-muted-foreground mb-1">
                        {t('registro.dialog.dpoAnalysisOf')}
                        {dialog.entry.incident.dpo_analyst_name
                          ? ` — ${dialog.entry.incident.dpo_analyst_name}`
                          : ''}
                      </p>
                      {dialog.entry.incident.dpo_notes}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>{t('registro.dialog.decision')}</Label>
                    <Select value={decision} onValueChange={(v) => setDecision(v as 'RIPRISTINO' | 'REVOCA')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RIPRISTINO">{t('registro.dialog.restoreOption')}</SelectItem>
                        <SelectItem value="REVOCA">{t('registro.dialog.revokeOption')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>
                  {dialog.kind === 'suspend' && t('registro.dialog.suspendLabel')}
                  {dialog.kind === 'dpo' && t('registro.dialog.dpoLabel')}
                  {dialog.kind === 'decide' && t('registro.dialog.decideLabel')}
                  {dialog.kind === 'lift' && t('registro.dialog.liftLabel')}
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder={
                    dialog.kind === 'suspend'
                      ? t('registro.dialog.suspendPlaceholder')
                      : undefined
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>{t('common.cancel')}</Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!notesValid || mutation.isPending}
              variant={dialog?.kind === 'suspend' || (dialog?.kind === 'decide' && decision === 'REVOCA') ? 'destructive' : 'default'}
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {dialog ? t(ACTION_TITLE_KEYS[dialog.kind] as any).split(' ')[0] : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
